import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import NewsSlider from "../../components/NewsSlider";
import {
  Actualite,
  Minister,
  Announcement,
  SliderApiData,
  BibliothequeItem,
  SousEtablissement,
  NewsItem, // Added NewsItem
} from "../../types/types";
import {
  getNews,
  getMinisters,
  getAnnouncements,
  getSliders,
  getBibliothequeItems,
  getSousEtablissements,
} from "../../services/api";
import { useLanguage } from "../../context/LanguageContext";
import { getTranslation } from "../../utils/translations";
import { resolveMediaUrl } from "../../utils/media";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendarAlt,
  faArrowRight,
  faNewspaper,
  faBullhorn,
  faChevronLeft,
  faChevronRight,
  faImages,
  faVideo,
  faUniversity,
  faExternalLinkAlt,
  faPlayCircle,
  faExpand,
  faTimes,
  faPlay,
  faDownload,
  faList,
  faSpinner,
  faSearch,
  faFilter,
  faEye,
  faChevronUp,
  faChevronDown,
  faUserTie,
} from "@fortawesome/free-solid-svg-icons";

// Fallback hero image
import heroImage from "../../assets/slide.jpg";

// YouTube Embed Component
const YouTubeEmbed = ({
  videoId,
  autoplay = false,
}: {
  videoId: string;
  autoplay?: boolean;
}) => {
  return (
    <div className="relative w-full" style={{ paddingBottom: "56.25%" }}>
      <iframe
        className="absolute top-0 left-0 w-full h-full rounded-lg"
        src={`https://www.youtube.com/embed/${videoId}?autoplay=${
          autoplay ? 1 : 0
        }&modestbranding=1&rel=0&showinfo=0&controls=1`}
        title="YouTube video player"
        frameBorder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowFullScreen
      />
    </div>
  );
};

// Get YouTube video ID from URL
const getYouTubeId = (url: string): string | null => {
  const regExp =
    /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
  const match = url.match(regExp);
  return match && match[2].length === 11 ? match[2] : null;
};

// Get YouTube thumbnail
const getYouTubeThumbnail = (url: string): string => {
  const videoId = getYouTubeId(url);
  if (videoId) {
    return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
  }
  return "https://via.placeholder.com/300x200/007a45/FFFFFF?text=Video";
};

const Home: React.FC = () => {
  const [newsItems, setNewsItems] = useState<Actualite[]>([]);
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [ministers, setMinisters] = useState<Minister[]>([]);
  const [sliders, setSliders] = useState<SliderApiData[]>([]);
  const [mediaItems, setMediaItems] = useState<BibliothequeItem[]>([]);
  const [sousEtablissements, setSousEtablissements] = useState<
    SousEtablissement[]
  >([]);

  const [loading, setLoading] = useState(true);
  const [announcementLoading, setAnnouncementLoading] = useState(true);
  const [ministerLoading, setMinisterLoading] = useState(true);
  const [sliderLoading, setSliderLoading] = useState(true);
  const [mediaLoading, setMediaLoading] = useState(true);
  const [sousEtablissementsLoading, setSousEtablissementsLoading] =
    useState(true);

  const [currentSlide, setCurrentSlide] = useState(0);
  const [activeMediaTab, setActiveMediaTab] = useState<"images" | "videos">(
    "images"
  );

  // States for image gallery modal
  const [selectedPhoto, setSelectedPhoto] = useState<BibliothequeItem | null>(
    null
  );
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [expandedImage, setExpandedImage] = useState<number | null>(null);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);

  // States for video modal
  const [selectedVideo, setSelectedVideo] = useState<BibliothequeItem | null>(
    null
  );
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [showVideoList, setShowVideoList] = useState(false);

  // Animation states
  const [isMinistryVisible, setIsMinistryVisible] = useState(false);
  const [isAnnouncementsVisible, setIsAnnouncementsVisible] = useState(false);
  const [isNewsVisible, setIsNewsVisible] = useState(false);
  const [isMediaVisible, setIsMediaVisible] = useState(false);
  const [isSousEtablissementsVisible, setIsSousEtablissementsVisible] =
    useState(false);

  const { language } = useLanguage();

  // Refs for intersection observer
  const ministryRef = useRef<HTMLElement>(null);
  const announcementsRef = useRef<HTMLElement>(null);
  const newsRef = useRef<HTMLElement>(null);
  const mediaRef = useRef<HTMLElement>(null);
  const sousEtablissementsRef = useRef<HTMLElement>(null);
  // Add this with other useState declarations at the top of the component
  const [currentSousEtablissement, setCurrentSousEtablissement] = useState(0);
  const [itemsPerSlide, setItemsPerSlide] = useState(4); // Default for large screens

  // Add this useEffect to handle responsive items per slide
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setItemsPerSlide(1);
      } else if (window.innerWidth < 1024) {
        setItemsPerSlide(2);
      } else if (window.innerWidth < 1280) {
        setItemsPerSlide(3);
      } else {
        setItemsPerSlide(4);
      }
    };

    handleResize(); // Initial call
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Carousel navigation functions
  const nextSousEtablissement = () => {
    const maxSlides = Math.ceil(sousEtablissements.length / itemsPerSlide);
    setCurrentSousEtablissement((prev) => (prev + 1) % maxSlides);
  };

  const prevSousEtablissement = () => {
    const maxSlides = Math.ceil(sousEtablissements.length / itemsPerSlide);
    setCurrentSousEtablissement((prev) => (prev - 1 + maxSlides) % maxSlides);
  };

  // Update the getSousEtablissementsStaggerAnimationClass to work with carousel

  useEffect(() => {
    fetchNews();
    fetchAnnouncements();
    fetchMinisters();
    fetchSliders();
    fetchMediaItems();
    fetchSousEtablissements();
  }, []);

  // Intersection Observer for scroll animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            if (entry.target.id === "ministry") {
              setIsMinistryVisible(true);
            } else if (entry.target.id === "announcements") {
              setIsAnnouncementsVisible(true);
            } else if (entry.target.id === "news") {
              setIsNewsVisible(true);
            } else if (entry.target.id === "media") {
              setIsMediaVisible(true);
            } else if (entry.target.id === "sous-etablissements") {
              setIsSousEtablissementsVisible(true);
            }
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: "0px 0px -50px 0px",
      }
    );

    if (ministryRef.current) observer.observe(ministryRef.current);
    if (announcementsRef.current) observer.observe(announcementsRef.current);
    if (newsRef.current) observer.observe(newsRef.current);
    if (mediaRef.current) observer.observe(mediaRef.current);
    if (sousEtablissementsRef.current)
      observer.observe(sousEtablissementsRef.current);

    return () => observer.disconnect();
  }, []);

  // Auto-advance slides
  useEffect(() => {
    if (sliders.length > 1) {
      const interval = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % sliders.length);
      }, 5000); // Change slide every 5 seconds
      return () => clearInterval(interval);
    }
  }, [sliders.length]);

  // Handle keyboard navigation for modals
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Image gallery navigation
      if (selectedPhoto && selectedPhoto.fichiers) {
        if (e.key === "ArrowRight") {
          nextImage();
        } else if (e.key === "ArrowLeft") {
          prevImage();
        } else if (e.key === "Escape") {
          closePhotoModal();
        }
      }

      // Video modal navigation
      if (selectedVideo && selectedVideo.liens) {
        if (e.key === "ArrowRight") {
          nextVideo();
        } else if (e.key === "ArrowLeft") {
          prevVideo();
        } else if (e.key === "Escape") {
          closeVideoModal();
        } else if (e.key === "l" || e.key === "L") {
          setShowVideoList(!showVideoList);
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [
    selectedPhoto,
    currentImageIndex,
    selectedVideo,
    currentVideoIndex,
    showVideoList,
  ]);

  // دالة لتنظيف النص من الوسوم HTML
  const cleanHtmlTags = (text: string) => {
    if (!text) return "";
    return text
      .replace(/<p><p>/g, "")
      .replace(/<p>/g, "")
      .replace(/<\/p>/g, "\n")
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(/&nbsp;/g, " ")
      .replace(/<[^>]*>/g, "")
      .trim();
  };

  const fetchSliders = async () => {
    try {
      setSliderLoading(true);
      const response = await getSliders();
      // Filter only active sliders and sort by order
      const activeSliders = response.data
        .filter((slider: SliderApiData) => slider.etat) // Only active sliders
        .sort(
          (a: SliderApiData, b: SliderApiData) =>
            (a.ordre || 0) - (b.ordre || 0)
        );
      setSliders(activeSliders);
    } catch (error) {
      console.error("Error fetching sliders:", error);
    } finally {
      setSliderLoading(false);
    }
  };

  const fetchNews = async () => {
    try {
      setLoading(true);
      const response = await getNews();

      // Sort news by date in descending order (most recent first)
      const sortedNews = response.data.sort((a: Actualite, b: Actualite) => {
        const dateA = new Date(a.datepublication || 0).getTime();
        const dateB = new Date(b.datepublication || 0).getTime();
        return dateB - dateA; // Descending order
      });

      const cleanedNews = sortedNews.map((news: Actualite) => ({
        ...news,
        textarabe: cleanHtmlTags(news.textarabe || ""),
        textfrancais: cleanHtmlTags(news.textfrancais || ""),
      }));
      setNewsItems(cleanedNews);
    } catch (error) {
      console.error("Error fetching news:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAnnouncements = async () => {
    try {
      setAnnouncementLoading(true);
      const response = await getAnnouncements();
      const cleanedAnnouncements = response.data.map(
        (announcement: Announcement) => ({
          ...announcement,
          textarabe: cleanHtmlTags(announcement.textarabe || ""),
          textfrancais: cleanHtmlTags(announcement.textfrancais || ""),
        })
      );
      const latestAnnouncements = cleanedAnnouncements.slice(0, 3);
      setAnnouncements(latestAnnouncements);
    } catch (error) {
      console.error("Error fetching announcements:", error);
    } finally {
      setAnnouncementLoading(false);
    }
  };

  const fetchMinisters = async () => {
    try {
      setMinisterLoading(true);
      const response = await getMinisters();
      if (response.data && response.data.length > 0) {
        const firstMinister = response.data[0];

        const ministerData: Minister = {
          ...firstMinister,
          textarabe: firstMinister.textarabe || "",
          textfrancais: firstMinister.textfrancais || "",
          image:
            resolveMediaUrl(firstMinister.image) ||
            "https://via.placeholder.com/250x300/007a45/FFFFFF?text=وزير+العقارات",
        };

        setMinisters([ministerData]);
      }
    } catch (error) {
      console.error("Error fetching ministers:", error);
    } finally {
      setMinisterLoading(false);
    }
  };

  const fetchMediaItems = async () => {
    try {
      setMediaLoading(true);
      const response = await getBibliothequeItems();

      // Filter items that have either files or links
      const filteredItems = response.data.filter(
        (item: BibliothequeItem) =>
          (item.fichiers && item.fichiers.length > 0) ||
          (item.liens && item.liens.length > 0)
      );

      // Sort by order and limit to 6 items
      const sortedItems = filteredItems
        .sort(
          (a: BibliothequeItem, b: BibliothequeItem) =>
            (a.ordre || 0) - (b.ordre || 0)
        )
        .slice(0, 6);

      setMediaItems(sortedItems);
    } catch (error) {
      console.error("Error fetching media items:", error);
    } finally {
      setMediaLoading(false);
    }
  };

  const fetchSousEtablissements = async () => {
    try {
      setSousEtablissementsLoading(true);
      const response = await getSousEtablissements();

      // Sort by title and limit to 4 items
      const sortedItems = response.data
        .sort((a: SousEtablissement, b: SousEtablissement) =>
          a.titrearabe.localeCompare(b.titrearabe)
        )
        .slice(0, 4);

      setSousEtablissements(sortedItems);
    } catch (error) {
      console.error("Error fetching sous-établissements:", error);
    } finally {
      setSousEtablissementsLoading(false);
    }
  };

  // Helper function to get slider image URL
  const getSliderImageUrl = (image: string | null | undefined): string =>
    resolveMediaUrl(image) || heroImage;

  // Helper function to get text based on language
  const getText = (arabicText: string, frenchText: string) => {
    return language === "ar" ? arabicText : frenchText;
  };

  // Helper function to get title based on language
  const getTitle = (arabicTitle: string, frenchTitle: string) => {
    return language === "ar" ? arabicTitle : frenchTitle;
  };

  // Direction class based on language
  const getDirectionClass = () => {
    return language === "ar" ? "rtl" : "ltr";
  };

  // Text alignment class based on language
  const getTextAlignClass = () => {
    return language === "ar" ? "text-right" : "text-left";
  };

  // Navigation functions for slider
  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % sliders.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + sliders.length) % sliders.length);
  };

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };

  // Image gallery functions
  const openPhotoModal = (photo: BibliothequeItem) => {
    setSelectedPhoto(photo);
    setCurrentImageIndex(0);
    document.body.style.overflow = "hidden";
  };

  const closePhotoModal = () => {
    setSelectedPhoto(null);
    setCurrentImageIndex(0);
    document.body.style.overflow = "unset";
  };

  const nextImage = () => {
    if (selectedPhoto && selectedPhoto.fichiers) {
      const nextIndex = (currentImageIndex + 1) % selectedPhoto.fichiers.length;
      setCurrentImageIndex(nextIndex);
    }
  };

  const prevImage = () => {
    if (selectedPhoto && selectedPhoto.fichiers) {
      const prevIndex =
        currentImageIndex === 0
          ? selectedPhoto.fichiers.length - 1
          : currentImageIndex - 1;
      setCurrentImageIndex(prevIndex);
    }
  };

  const goToImage = (index: number) => {
    setCurrentImageIndex(index);
  };

  const downloadImage = () => {
    if (!selectedPhoto?.fichiers?.[currentImageIndex]) return;

    const link = document.createElement("a");
    link.href = resolveMediaUrl(selectedPhoto.fichiers[currentImageIndex]);
    link.download = `photo-${selectedPhoto.id}-${currentImageIndex + 1}.jpg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getImageUrl = (fileName: string) => resolveMediaUrl(fileName);

  // Video modal functions
  const openVideoModal = (video: BibliothequeItem) => {
    setSelectedVideo(video);
    setCurrentVideoIndex(0);
    setShowVideoList(video.liens && video.liens.length > 1);
    document.body.style.overflow = "hidden";
  };

  const closeVideoModal = () => {
    setSelectedVideo(null);
    setCurrentVideoIndex(0);
    setShowVideoList(false);
    document.body.style.overflow = "unset";
  };

  const nextVideo = () => {
    if (selectedVideo && selectedVideo.liens) {
      const nextIndex = (currentVideoIndex + 1) % selectedVideo.liens.length;
      setCurrentVideoIndex(nextIndex);
    }
  };

  const prevVideo = () => {
    if (selectedVideo && selectedVideo.liens) {
      const prevIndex =
        currentVideoIndex === 0
          ? selectedVideo.liens.length - 1
          : currentVideoIndex - 1;
      setCurrentVideoIndex(prevIndex);
    }
  };

  const goToVideo = (index: number) => {
    setCurrentVideoIndex(index);
  };

  const getCurrentVideoId = () => {
    if (!selectedVideo?.liens?.[currentVideoIndex]) return null;
    return getYouTubeId(selectedVideo.liens[currentVideoIndex]);
  };

  const getCurrentVideoUrl = () => {
    if (!selectedVideo?.liens?.[currentVideoIndex]) return "";
    return selectedVideo.liens[currentVideoIndex];
  };

  // Handle touch events for swipe in image gallery
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) {
      nextImage();
    } else if (isRightSwipe) {
      prevImage();
    }

    // Reset touch positions
    setTouchStart(0);
    setTouchEnd(0);
  };

  // Animation classes
  const getMinistryAnimationClass = () => {
    return isMinistryVisible ? "animate-fade-in-up" : "opacity-0 translate-y-8";
  };

  const getAnnouncementsAnimationClass = () => {
    return isAnnouncementsVisible
      ? "animate-fade-in-up"
      : "opacity-0 translate-y-8";
  };

  const getNewsAnimationClass = () => {
    return isNewsVisible ? "animate-fade-in-up" : "opacity-0 translate-y-8";
  };

  const getMediaAnimationClass = () => {
    return isMediaVisible ? "animate-fade-in-up" : "opacity-0 translate-y-8";
  };

  const getSousEtablissementsAnimationClass = () => {
    return isSousEtablissementsVisible
      ? "animate-fade-in-up"
      : "opacity-0 translate-y-8";
  };

  const getStaggerAnimationClass = (index: number) => {
    return isAnnouncementsVisible
      ? `animate-fade-in-up delay-${index * 100}`
      : "opacity-0 translate-y-8";
  };

  const getNewsStaggerAnimationClass = (index: number) => {
    return isNewsVisible
      ? `animate-fade-in-up delay-${index * 100}`
      : "opacity-0 translate-y-8";
  };

  const getMediaStaggerAnimationClass = (index: number) => {
    return isMediaVisible
      ? `animate-fade-in-up delay-${index * 100}`
      : "opacity-0 translate-y-8";
  };

  const getSousEtablissementsStaggerAnimationClass = (index: number) => {
    return isSousEtablissementsVisible
      ? `animate-fade-in-up delay-${index * 100}`
      : "opacity-0 translate-y-8";
  };

  // Filter media items based on active tab
  const filteredMediaItems = mediaItems.filter((item) => {
    if (activeMediaTab === "images") {
      return item.fichiers && item.fichiers.length > 0;
    } else {
      return item.liens && item.liens.length > 0;
    }
  });
  const rawDescription =
    ministers && ministers.length > 0
      ? language === "ar"
        ? ministers[0].textarabe || ""
        : ministers[0].textfrancais || ""
      : "";

  // Fix formatting by replacing HTML block tags BEFORE stripping all tags
  const cleanText = rawDescription
    .replace(/<\/h[1-6]>/gi, "\n\n")
    .replace(/<h[1-6][^>]*>/gi, "")

    .replace(/<\/p>/gi, "\n\n")
    .replace(/<p[^>]*>/gi, "")

    .replace(/<li[^>]*>/gi, "• ")
    .replace(/<\/li>/gi, "\n")

    .replace(/<ul[^>]*>/gi, "")
    .replace(/<\/ul>/gi, "\n")

    .replace(/<br\s*\/?>/gi, "\n")

    // remove all remaining html tags
    .replace(/<[^>]+>/g, "")
    .trim();

  return (
    <div className={getDirectionClass()}>
      {/* Dynamic Hero Section with Slider - Fixed Height */}
      {/* Only render the section if we have sliders */}
      {sliders.length > 0 && (
        <section className="relative h-[80vh] min-h-[600px] max-h-[800px] overflow-hidden">
          {sliderLoading ? (
            // Enhanced Loading state
            <div
              className="w-full h-full bg-cover bg-center flex items-center justify-center"
              style={{ backgroundImage: `url(${heroImage})` }}
            >
              <div className="text-center text-white bg-black bg-opacity-60 p-10 rounded-2xl backdrop-blur-sm border border-white/20">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-mauritania-gold mb-6"></div>
              </div>
            </div>
          ) : (
            // Enhanced Slider with multiple slides
            <>
              {sliders.map((slider, index) => (
                <div
                  key={slider.id}
                  className={`absolute inset-0 transition-all duration-1000 ease-in-out transform ${
                    index === currentSlide
                      ? "opacity-100 z-10 scale-100"
                      : "opacity-0 z-0 scale-105"
                  }`}
                >
                  {/* Enhanced Background Image with parallax effect */}
                  <div
                    className="absolute inset-0 bg-cover bg-center bg-fixed"
                    style={{
                      backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.4)), url(${getSliderImageUrl(
                        slider.image
                      )})`,
                    }}
                  ></div>

                  {/* Enhanced Content Overlay with gradient */}
                  <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-transparent to-black/40 flex items-center justify-center">
                    <div className="container mx-auto max-w-7xl px-8 text-white">
                      <div className="max-w-6xl mx-auto">
                        {/* Title with decreased size but full width */}
                        <div className="w-full mb-6 md:mb-8 animate-fade-in-up">
                          <h2 className="text-3xl md:text-5xl lg:text-6xl font-black text-white leading-tight text-center w-full">
                            <span className="bg-gradient-to-r from-mauritania-gold to-yellow-300 bg-clip-text text-transparent block w-full">
                              {getTitle(
                                slider.titrearabe,
                                slider.titrefrancais
                              )}
                            </span>
                          </h2>
                        </div>
                        {/* Enhanced Description with full width */}
                        <div className="w-full bg-black/40 backdrop-blur-sm rounded-2xl p-6 md:p-8 border border-white/10 mb-8 animate-fade-in-up delay-200">
                          <p className="text-xl md:text-2xl lg:text-2xl leading-relaxed font-medium text-gray-100 text-center w-full">
                            {getText(slider.textarabe, slider.textfrancais)}
                          </p>
                        </div>
                        {/* Enhanced Slider Buttons */}
                        {slider.buttons && slider.buttons.length > 0 && (
                          <div className="flex flex-col sm:flex-row justify-center gap-5 animate-fade-in-up delay-400 w-full">
                            {slider.buttons.map((button, btnIndex) => (
                              <Link
                                key={btnIndex}
                                to={button.lien || "#"}
                                className="group relative bg-gradient-to-r from-mauritania-gold to-yellow-500 text-mauritania-green-dark font-bold py-4 px-8 rounded-xl hover:shadow-2xl hover:scale-105 transition-all duration-300 text-lg md:text-xl transform hover:-translate-y-1"
                              >
                                <span className="relative z-10">
                                  {getTitle(
                                    button.titrearabe,
                                    button.titrefrancais
                                  )}
                                </span>
                                <div className="absolute inset-0 bg-gradient-to-r from-yellow-500 to-mauritania-gold rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                              </Link>
                            ))}
                          </div>
                        )}
                        {/* Enhanced Fallback buttons */}
                        {(!slider.buttons || slider.buttons.length === 0) && (
                          <div className="flex flex-col sm:flex-row justify-center gap-5 animate-fade-in-up delay-400 w-full">
                            <Link
                              to="#tasks"
                              className="group relative bg-gradient-to-r from-mauritania-gold to-yellow-500 text-mauritania-green-dark font-bold py-4 px-8 rounded-xl hover:shadow-2xl hover:scale-105 transition-all duration-300 text-lg md:text-xl transform hover:-translate-y-1"
                            >
                              <span className="relative z-10">
                                {getTranslation("learnMoreButton", language)}
                              </span>
                              <div className="absolute inset-0 bg-gradient-to-r from-yellow-500 to-mauritania-gold rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                            </Link>
                            <Link
                              to="#services"
                              className="group relative border-2 border-mauritania-gold text-mauritania-gold font-bold py-4 px-8 rounded-xl hover:bg-mauritania-gold hover:text-mauritania-green-dark transition-all duration-300 text-lg md:text-xl transform hover:-translate-y-1 hover:shadow-xl backdrop-blur-sm"
                            >
                              {getTranslation("servicesButton", language)}
                            </Link>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {/* Enhanced Navigation Arrows */}
              {sliders.length > 1 && (
                <>
                  <button
                    onClick={prevSlide}
                    className="absolute left-6 top-1/2 transform -translate-y-1/2 bg-black/60 backdrop-blur-sm text-mauritania-gold p-4 rounded-full hover:bg-mauritania-gold hover:text-mauritania-green-dark transition-all duration-300 z-20 hover:scale-110 hover:shadow-2xl border border-mauritania-gold/30"
                    aria-label="Previous slide"
                  >
                    <FontAwesomeIcon icon={faChevronLeft} className="w-6 h-6" />
                  </button>
                  <button
                    onClick={nextSlide}
                    className="absolute right-6 top-1/2 transform -translate-y-1/2 bg-black/60 backdrop-blur-sm text-mauritania-gold p-4 rounded-full hover:bg-mauritania-gold hover:text-mauritania-green-dark transition-all duration-300 z-20 hover:scale-110 hover:shadow-2xl border border-mauritania-gold/30"
                    aria-label="Next slide"
                  >
                    <FontAwesomeIcon
                      icon={faChevronRight}
                      className="w-6 h-6"
                    />
                  </button>
                </>
              )}

              {/* Enhanced Slide Indicators */}
              {sliders.length > 1 && (
                <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex space-x-3 z-20">
                  {sliders.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => goToSlide(index)}
                      className={`w-4 h-4 rounded-full transition-all duration-300 border-2 ${
                        index === currentSlide
                          ? "bg-mauritania-gold border-mauritania-gold scale-125"
                          : "bg-transparent border-white hover:bg-white/50 hover:scale-110"
                      }`}
                      aria-label={`Go to slide ${index + 1}`}
                    />
                  ))}
                </div>
              )}

              {/* Scroll indicator */}
              <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-20 animate-bounce">
                <div className="w-6 h-10 border-2 border-mauritania-gold rounded-full flex justify-center">
                  <div className="w-1 h-3 bg-mauritania-gold rounded-full mt-2 animate-pulse"></div>
                </div>
              </div>
            </>
          )}
        </section>
      )}

      {/* Ministry Section with Animation */}
      <section
        ref={ministryRef}
        className="py-8 px-8 bg-white overflow-hidden"
        id="ministry"
      >
        <div className="container mx-auto">
          <div
            className={`grid grid-cols-1 lg:grid-cols-10 gap-8 mb-8 transition-all duration-700 delay-300 ${getMinistryAnimationClass()}`}
          >
            {/* Minister Column (70%) */}
            <div className="w-full lg:col-span-7 h-full">
              {ministerLoading ? (
                <div className="text-center py-16 text-gray-600 flex flex-col justify-center h-full bg-white rounded-xl shadow-lg border border-gray-100 min-h-[400px]">
                  <FontAwesomeIcon
                    icon={faSpinner}
                    className="text-mauritania-green-dark text-4xl animate-spin mb-4 mx-auto"
                  />
                  <p>{getTranslation("loadingMinister", language)}</p>
                </div>
              ) : ministers.length > 0 ? (
                <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden h-full flex flex-col">
                  <div className="bg-mauritania-green-dark p-4 text-white flex justify-between items-center flex-shrink-0">
                    <h3 className="text-xl font-bold flex items-center gap-2">
                      <FontAwesomeIcon
                        icon={faUserTie}
                        className="text-mauritania-gold"
                      />
                      {getTranslation("ministerCV", language)}
                    </h3>
                  </div>
                  <div className="p-6 flex flex-col flex-grow">
                    {/* Restored layout: Image on side */}
                    <div className="flex flex-col md:flex-row gap-6 items-start mb-4 h-full">
                      <div className="w-full md:w-1/3 flex-shrink-0">
                        <div className="relative">
                          <img
                            src={
                              resolveMediaUrl(ministers[0].image) ||
                              "https://via.placeholder.com/300x400"
                            }
                            alt={
                              language === "ar"
                                ? ministers[0].titrearabe
                                : ministers[0].titrefrancais
                            }
                            className="w-full h-64 object-cover rounded-xl border-4 border-mauritania-gold shadow-md"
                          />
                        </div>
                        <div className="mt-4 text-center">
                          <h4 className="text-lg font-bold text-mauritania-green-dark">
                            {language === "ar"
                              ? ministers[0].titrearabe
                              : ministers[0].titrefrancais}
                          </h4>
                          <span className="inline-block text-xs font-medium text-white bg-mauritania-gold px-2 py-1 rounded-full mt-1">
                            {language === "ar"
                              ? "معالي الوزير"
                              : "Son Excellence le Ministre"}
                          </span>
                        </div>
                      </div>

                      <div className="w-full md:w-2/3 flex flex-col h-full">
                        <div className="text-gray-600 text-sm leading-relaxed mb-6 text-justify max-h-96 overflow-hidden relative">
                          {language === "ar" ? (
                            ministers[0].textarabe ? (
                              <div
                                className="prose prose-sm max-w-none text-right"
                                dangerouslySetInnerHTML={{
                                  __html: ministers[0].textarabe,
                                }}
                              ></div>
                            ) : null
                          ) : ministers[0].textfrancais ? (
                            <div
                              className="prose prose-sm max-w-none text-left"
                              dangerouslySetInnerHTML={{
                                __html: ministers[0].textfrancais,
                              }}
                            ></div>
                          ) : null}
                          <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-white to-transparent"></div>
                        </div>

                        <div className="mt-auto text-right">
                          <Link
                            to="/minister"
                            className="inline-flex items-center text-mauritania-green font-bold hover:text-mauritania-gold transition-colors"
                          >
                            <span>
                              {getTranslation("readFullCV", language) ||
                                (language === "ar"
                                  ? "قراءة السيرة الذاتية الكاملة"
                                  : "Lire le CV complet")}
                            </span>
                            <FontAwesomeIcon
                              icon={
                                language === "ar"
                                  ? faChevronLeft
                                  : faChevronRight
                              }
                              className="mx-2"
                            />
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12 bg-gray-50 rounded-xl h-full flex flex-col justify-center border border-gray-100">
                  <div className="bg-gray-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                    <FontAwesomeIcon
                      icon={faUserTie}
                      className="text-gray-400 text-2xl"
                    />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-700 mb-2">
                    {getTranslation("noMinisterData", language)}
                  </h3>
                  <p className="text-gray-500 max-w-md mx-auto">
                    {language === "ar"
                      ? "سيتم تحديث معلومات الوزير قريباً. شكراً لتفهمكم."
                      : "Les informations du ministre seront mises à jour prochainement. Merci de votre compréhension."}
                  </p>
                </div>
              )}
            </div>

            {/* News Slider Column (30%) */}
            <div className="w-full lg:col-span-3 h-full">
              <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden h-full flex flex-col pb-6">
                <div className="bg-mauritania-gold p-4 text-mauritania-green-dark flex justify-between items-center mb-4 flex-shrink-0">
                  <h3 className="text-xl font-bold flex items-center gap-2">
                    <FontAwesomeIcon
                      icon={faNewspaper}
                      className="text-white"
                    />
                    {getTranslation("recentNews", language)}
                  </h3>
                  <Link
                    to="/news"
                    className="text-sm font-bold text-white hover:text-mauritania-green-dark transition-colors flex items-center gap-1"
                  >
                    {getTranslation("moreNews", language)}
                    <FontAwesomeIcon
                      icon={
                        language === "ar" ? faChevronLeft : faChevronRight
                      }
                    />
                  </Link>
                </div>
                <div className="px-4 flex-grow flex flex-col">
                  {loading ? (
                    <div className="flex justify-center items-center flex-col h-full min-h-[200px] py-16">
                      <FontAwesomeIcon
                        icon={faSpinner}
                        className="text-mauritania-green-dark text-4xl animate-spin mb-4"
                      />
                    </div>
                  ) : (
                    <div className="flex-grow">
                      <NewsSlider
                        news={newsItems
                          .slice()
                          .sort(
                            (a, b) =>
                              new Date(b.datepublication).getTime() -
                              new Date(a.datepublication).getTime()
                          )
                          .slice(0, 5)
                          .map((item) => ({
                            id: item.id || 0,
                            title:
                              language === "ar"
                                ? item.titrearabe
                                : item.titrefrancais,
                            date: new Date(item.datepublication).toLocaleDateString(
                              language === "ar" ? "ar-EG" : "fr-FR",
                              {
                                year: "numeric",
                                month: "long",
                                day: "numeric",
                              }
                            ),
                            excerpt:
                              language === "ar"
                                ? (item.textarabe?.substring(0, 120) || "") +
                                  (item.textarabe?.length > 120 ? "..." : "")
                                : (item.textfrancais?.substring(0, 120) || "") +
                                  (item.textfrancais?.length > 120 ? "..." : ""),
                            image:
                              resolveMediaUrl(item.image) ||
                              "https://via.placeholder.com/300x200",
                          }))}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Enhanced News Section with Animation */}
      <section
        ref={newsRef}
        className="py-8 px-8 bg-light-bg overflow-hidden"
        id="news"
      >
        <div className="container mx-auto">
          {/* Section Title with new style */}
          <div className="mb-12">
            <div className="flex items-center gap-4 mb-4">
              <div className="relative">
                <div className="w-14 h-14 bg-mauritania-green-dark rounded-full flex items-center justify-center shadow-lg">
                  <FontAwesomeIcon
                    icon={faNewspaper}
                    className="text-mauritania-gold text-2xl"
                  />
                </div>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-mauritania-green-dark">
                {getTranslation("ministryNews", language)}
              </h2>
            </div>

            {/* Full width separator line */}
            <div className="relative w-full h-1">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full h-px bg-gradient-to-r from-transparent via-mauritania-gold to-transparent"></div>
              </div>
              <div className="absolute left-1/2 transform -translate-x-1/2 -bottom-1 w-16 h-1 bg-mauritania-gold rounded-full"></div>
            </div>
          </div>

          {/* Content */}
          {loading ? (
            <div className="text-center py-16 text-gray-600">
              <FontAwesomeIcon
                icon={faSpinner}
                className="text-mauritania-green-dark text-4xl animate-spin mb-4"
              />
              <p>{getTranslation("loadingNews", language)}</p>
            </div>
          ) : newsItems.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {newsItems
                .slice() // avoid mutating original array
                .sort(
                  (a, b) =>
                    new Date(b.datepublication).getTime() -
                    new Date(a.datepublication).getTime()
                )
                .slice(0, 6)
                .map((news, index) => (
                  <div
                    key={news.id}
                    className={`bg-white rounded-lg overflow-hidden shadow-custom hover:shadow-xl transition-all duration-500 hover:-translate-y-2 flex flex-col h-full ${getNewsStaggerAnimationClass(
                      index
                    )}`}
                  >
                    {/* Image */}
                    <Link
                      to={`/news/${news.id}`}
                      className="relative h-0 pb-[56.25%] overflow-hidden block group"
                    >
                      <img
                        src={
                          resolveMediaUrl(news.image) ||
                          "https://images.unsplash.com/photo-1588681664899-f142ff2dc9b1?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
                        }
                        alt={getTitle(news.titrearabe, news.titrefrancais)}
                        className="absolute inset-0 w-full h-full object-cover transition duration-700 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-black/20 group-hover:bg-black/0 transition-colors duration-500"></div>
                      <div className="absolute top-4 left-4 bg-mauritania-gold text-mauritania-green-dark font-semibold py-1 px-3 rounded-full text-sm shadow-md">
                        {getTranslation("news", language)}
                      </div>
                    </Link>

                    {/* Body */}
                    <div className="p-6 flex flex-col flex-grow">
                      <div className="flex items-center text-sm text-mauritania-gold mb-3">
                        <FontAwesomeIcon
                          icon={faCalendarAlt}
                          className="mr-2"
                        />
                        {new Date(news.datepublication).toLocaleDateString(
                          language === "ar" ? "ar-EG" : "fr-FR",
                          {
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                          }
                        )}
                      </div>

                      <h3 className="text-xl font-bold text-mauritania-green-dark mb-4 line-clamp-2 hover:text-mauritania-green transition">
                        {getTitle(news.titrearabe, news.titrefrancais)}
                      </h3>

                      <div className="text-gray-600 mb-6 whitespace-pre-line line-clamp-3 flex-grow">
                        {language === "ar"
                          ? (news.textarabe?.substring(0, 120) || "") +
                            (news.textarabe?.length > 120 ? "..." : "")
                          : (news.textfrancais?.substring(0, 120) || "") +
                            (news.textfrancais?.length > 120 ? "..." : "")}
                      </div>

                      <Link
                        to={`/news/${news.id}`}
                        className="inline-flex items-center text-mauritania-green-dark font-semibold hover:text-mauritania-green transition group mt-auto"
                      >
                        {getTranslation("readMore", language)}
                        <FontAwesomeIcon
                          icon={faArrowRight}
                          className={`ml-2 transition-transform duration-300 group-hover:translate-x-2 ${
                            language === "ar"
                              ? "rotate-180 group-hover:-translate-x-2"
                              : ""
                          }`}
                        />
                      </Link>
                    </div>
                  </div>
                ))}
            </div>
          ) : (
            <div className="text-center py-12 text-gray-600">
              {getTranslation("noNews", language)}
            </div>
          )}

          {/* More News Button */}
          <div
            className={`text-center mt-12 transition-all duration-700 delay-500 ${getNewsAnimationClass()}`}
          >
            <Link
              to="/news"
              className="inline-flex items-center bg-mauritania-gold text-mauritania-green-dark font-semibold py-3 px-8 rounded hover:bg-yellow-600 transition duration-300 group hover:scale-105 hover:shadow-lg"
            >
              {getTranslation("moreNews", language)}
              <FontAwesomeIcon
                icon={faArrowRight}
                className={`ml-2 transition-transform duration-300 group-hover:translate-x-2 ${
                  language === "ar"
                    ? "rotate-180 group-hover:-translate-x-2"
                    : ""
                }`}
              />
            </Link>
          </div>
        </div>
      </section>

      {/* Media Section - صور وفيديوهات */}
      <section
        ref={mediaRef}
        className="py-8 px-8 bg-white overflow-hidden"
        id="media"
      >
        <div className="container mx-auto">
          {/* Section Title with new style */}
          <div className="mb-12">
            <div className="flex items-center gap-4 mb-4">
              <div className="relative">
                <div className="w-14 h-14 bg-mauritania-green-dark rounded-full flex items-center justify-center shadow-lg">
                  <div className="relative">
                    <FontAwesomeIcon
                      icon={faImages}
                      className="text-mauritania-gold text-xl"
                    />
                    <FontAwesomeIcon
                      icon={faVideo}
                      className="text-mauritania-gold text-sm absolute -top-2 -right-2"
                    />
                  </div>
                </div>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-mauritania-green-dark">
                {getTranslation("mediaGallery", language) ||
                  "الصور والفيديوهات"}
              </h2>
            </div>

            {/* Full width separator line */}
            <div className="relative w-full h-1">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full h-px bg-gradient-to-r from-transparent via-mauritania-gold to-transparent"></div>
              </div>
              <div className="absolute left-1/2 transform -translate-x-1/2 -bottom-1 w-16 h-1 bg-mauritania-gold rounded-full"></div>
            </div>
          </div>

          {/* Media Tabs */}
          <div
            className={`mb-8 flex justify-center transition-all duration-700 delay-200 ${getMediaAnimationClass()}`}
          >
            <div className="inline-flex rounded-lg border border-gray-200 overflow-hidden">
              <button
                onClick={() => setActiveMediaTab("images")}
                className={`px-6 py-3 text-sm font-medium transition-all duration-300 ${
                  activeMediaTab === "images"
                    ? "bg-mauritania-green-dark text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                <FontAwesomeIcon icon={faImages} className="ml-2" />
                {getTranslation("images", language) || "الصور"}
              </button>
              <button
                onClick={() => setActiveMediaTab("videos")}
                className={`px-6 py-3 text-sm font-medium transition-all duration-300 ${
                  activeMediaTab === "videos"
                    ? "bg-mauritania-green-dark text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                <FontAwesomeIcon icon={faVideo} className="ml-2" />
                {getTranslation("videos", language) || "الفيديوهات"}
              </button>
            </div>
          </div>

          {mediaLoading ? (
            <div className="text-center py-16 text-gray-600">
              <FontAwesomeIcon
                icon={faSpinner}
                className="text-mauritania-green-dark text-4xl animate-spin mb-4"
              />
              <p>{getTranslation("loading", language) || "جاري التحميل..."}</p>
            </div>
          ) : filteredMediaItems.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredMediaItems.slice(0, 3).map((item, index) => (
                <div
                  key={item.id}
                  className={`bg-white rounded-lg overflow-hidden shadow-custom hover:shadow-xl transition-all duration-500 hover:-translate-y-2 flex flex-col h-full ${getMediaStaggerAnimationClass(
                    index
                  )}`}
                >
                  {/* Media Content */}
                  {activeMediaTab === "images" &&
                  item.fichiers &&
                  item.fichiers.length > 0 ? (
                    <div className="relative h-64 overflow-hidden">
                      <img
                        src={getImageUrl(item.fichiers[0])}
                        alt={getText(
                          item.descriptionarabe,
                          item.descriptionfrancais
                        )}
                        className="w-full h-full object-cover transition duration-700 hover:scale-110 cursor-pointer"
                        onClick={() => openPhotoModal(item)}
                      />
                      <div
                        className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-20 transition-all duration-300 flex items-center justify-center cursor-pointer"
                        onClick={() => openPhotoModal(item)}
                      >
                        <FontAwesomeIcon
                          icon={faExpand}
                          className="text-white text-3xl opacity-0 hover:opacity-100 transition-opacity duration-300"
                        />
                      </div>
                      {item.fichiers.length > 1 && (
                        <div className="absolute top-2 left-2 bg-green-500 text-white text-xs px-2 py-1 rounded">
                          {item.fichiers.length}{" "}
                          {language === "ar" ? "صورة" : "photos"}
                        </div>
                      )}
                    </div>
                  ) : activeMediaTab === "videos" &&
                    item.liens &&
                    item.liens.length > 0 ? (
                    <div className="relative h-64 overflow-hidden">
                      <img
                        src={getYouTubeThumbnail(item.liens[0])}
                        alt={getText(
                          item.descriptionarabe,
                          item.descriptionfrancais
                        )}
                        className="w-full h-full object-cover cursor-pointer"
                        onClick={() => openVideoModal(item)}
                      />
                      <div
                        className="absolute inset-0 bg-black bg-opacity-30 hover:bg-opacity-40 transition-all duration-300 flex items-center justify-center cursor-pointer"
                        onClick={() => openVideoModal(item)}
                      >
                        <div className="bg-red-600 hover:bg-red-700 text-white rounded-full w-16 h-16 flex items-center justify-center transition-all duration-300 transform hover:scale-110">
                          <FontAwesomeIcon
                            icon={faPlayCircle}
                            className="text-2xl"
                          />
                        </div>
                      </div>
                      {item.liens.length > 1 && (
                        <div className="absolute top-2 left-2 bg-red-500 text-white text-xs px-2 py-1 rounded">
                          {item.liens.length}{" "}
                          {language === "ar" ? "فيديو" : "vidéos"}
                        </div>
                      )}
                    </div>
                  ) : null}

                  <div className="p-6 flex flex-col flex-grow">
                    <h3 className="text-xl font-bold text-mauritania-green-dark mb-4 line-clamp-2">
                      {getText(item.descriptionarabe, item.descriptionfrancais)}
                    </h3>

                    <div className="mt-auto pt-4 border-t border-gray-100">
                      <button
                        onClick={() =>
                          activeMediaTab === "images"
                            ? openPhotoModal(item)
                            : openVideoModal(item)
                        }
                        className="inline-flex items-center text-mauritania-green-dark font-semibold hover:text-mauritania-green transition duration-300 group w-full justify-center"
                      >
                        <FontAwesomeIcon
                          icon={activeMediaTab === "images" ? faImages : faPlay}
                          className="ml-2"
                        />
                        {activeMediaTab === "images"
                          ? getTranslation("viewGallery", language) ||
                            "عرض المعرض"
                          : getTranslation("watchVideo", language) ||
                            "مشاهدة الفيديو"}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-gray-600">
              <FontAwesomeIcon
                icon={activeMediaTab === "images" ? faImages : faVideo}
                className="text-gray-400 text-4xl mb-4"
              />
              <p>
                {activeMediaTab === "images"
                  ? getTranslation("noImages", language) || "لا توجد صور متاحة"
                  : getTranslation("noVideos", language) ||
                    "لا توجد فيديوهات متاحة"}
              </p>
            </div>
          )}

          <div
            className={`text-center mt-12 transition-all duration-700 delay-500 ${getMediaAnimationClass()}`}
          >
            <Link
              to={activeMediaTab === "images" ? "/photos" : "/videos"}
              className="inline-flex items-center bg-mauritania-gold text-mauritania-green-dark font-semibold py-3 px-8 rounded hover:bg-yellow-600 transition duration-300 group hover:scale-105 hover:shadow-lg"
            >
              {activeMediaTab === "images"
                ? getTranslation("viewAllPhotos", language) || "عرض جميع الصور"
                : getTranslation("viewAllVideos", language) ||
                  "عرض جميع الفيديوهات"}
              <FontAwesomeIcon
                icon={faArrowRight}
                className={`ml-2 transition-transform duration-300 group-hover:translate-x-2 ${
                  language === "ar"
                    ? "rotate-180 group-hover:-translate-x-2"
                    : ""
                }`}
              />
            </Link>
          </div>
        </div>
      </section>
      {/* Announcements Section with Animation */}
      <section
        ref={announcementsRef}
        className="py-8 px-8 bg-mauritania-green-dark text-white overflow-hidden"
        id="announcements"
      >
        <div className="container mx-auto">
          <div
            className={`text-center mb-12 relative transition-all duration-700 ${getAnnouncementsAnimationClass()}`}
          >
            <div className="flex items-center justify-center mb-4">
              <FontAwesomeIcon
                icon={faBullhorn}
                className="text-mauritania-gold text-4xl mr-3 transition-transform duration-500 hover:scale-110"
              />
              <h2 className="text-3xl font-bold text-mauritania-gold inline-block pb-2">
                {getTranslation("officialAnnouncements", language)}
              </h2>
            </div>
            <div className="absolute bottom-0 right-1/2 transform translate-x-1/2 w-20 h-1 bg-mauritania-gold"></div>
          </div>

          {announcementLoading ? (
            <div className="text-center py-16 text-white">
              <FontAwesomeIcon
                icon={faSpinner}
                className="text-mauritania-gold text-4xl animate-spin mb-4"
              />
              <p>{getTranslation("loadingAnnouncements", language)}</p>
            </div>
          ) : announcements.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {announcements.map((announcement, index) => (
                <div
                  key={announcement.id}
                  className={`bg-white text-mauritania-green-dark rounded-lg overflow-hidden shadow-custom hover:shadow-xl transition-all duration-500 hover:-translate-y-2 flex flex-col h-full ${getStaggerAnimationClass(
                    index
                  )}`}
                >
                  <div className="p-6 flex flex-col flex-grow">
                    <div className="flex items-center text-sm text-mauritania-gold font-semibold mb-2">
                      <FontAwesomeIcon icon={faCalendarAlt} className="mr-2" />
                      {new Date(
                        announcement.datepublication
                      ).toLocaleDateString(
                        language === "ar" ? "ar-EG" : "fr-FR"
                      )}
                    </div>
                    <h3 className="text-xl font-bold mb-4 line-clamp-2 transition-colors duration-300 hover:text-mauritania-green">
                      {getTitle(
                        announcement.titrearabe,
                        announcement.titrefrancais
                      )}
                    </h3>
                    <div className="text-gray-600 mb-6 whitespace-pre-line line-clamp-3 flex-grow">
                      {language === "ar"
                        ? (announcement.textarabe?.substring(0, 100) || "") +
                          (announcement.textarabe?.length > 100 ? "..." : "")
                        : (announcement.textfrancais?.substring(0, 100) || "") +
                          (announcement.textfrancais?.length > 100
                            ? "..."
                            : "")}
                    </div>

                    <Link
                      to={`/announcements/${announcement.id}`}
                      className="inline-flex items-center text-mauritania-green font-semibold hover:text-mauritania-green-dark transition duration-300 mt-auto group"
                    >
                      {getTranslation("fullDetails", language)}
                      <FontAwesomeIcon
                        icon={faArrowRight}
                        className={`ml-2 transition-transform duration-300 group-hover:translate-x-2 ${
                          language === "ar"
                            ? "rotate-180 group-hover:-translate-x-2"
                            : ""
                        }`}
                      />
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-mauritania-gold">
              {getTranslation("noAnnouncements", language)}
            </div>
          )}

          <div
            className={`text-center mt-12 transition-all duration-700 delay-500 ${getAnnouncementsAnimationClass()}`}
          >
            <Link
              to="/announcements"
              className="inline-flex items-center bg-mauritania-gold text-mauritania-green-dark font-semibold py-3 px-8 rounded hover:bg-yellow-600 transition duration-300 group hover:scale-105 hover:shadow-lg"
            >
              {getTranslation("moreAnnouncements", language)}
              <FontAwesomeIcon
                icon={faArrowRight}
                className={`ml-2 transition-transform duration-300 group-hover:translate-x-2 ${
                  language === "ar"
                    ? "rotate-180 group-hover:-translate-x-2"
                    : ""
                }`}
              />
            </Link>
          </div>
        </div>
      </section>

      {/* Sous-établissements Section - مؤسسات تحت الوصاية */}

      {/* Sous-établissements Section - مؤسسات تحت الوصاية */}
      <section
        ref={sousEtablissementsRef}
        className="py-8 px-8 bg-light-bg overflow-hidden"
        id="sous-etablissements"
      >
        <div className="container mx-auto">
          {/* Section Title with new style */}
          <div className="mb-12">
            <div className="flex items-center gap-4 mb-4">
              <div className="relative">
                <div className="w-14 h-14 bg-mauritania-green-dark rounded-full flex items-center justify-center shadow-lg">
                  <FontAwesomeIcon
                    icon={faUniversity}
                    className="text-mauritania-gold text-2xl"
                  />
                </div>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-mauritania-green-dark">
                {getTranslation("sousEtablissements", language) ||
                  "المؤسسات تحت الوصاية"}
              </h2>
            </div>

            {/* Full width separator line */}
            <div className="relative w-full h-1">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full h-px bg-gradient-to-r from-transparent via-mauritania-gold to-transparent"></div>
              </div>
              <div className="absolute left-1/2 transform -translate-x-1/2 -bottom-1 w-16 h-1 bg-mauritania-gold rounded-full"></div>
            </div>
          </div>

          {sousEtablissementsLoading ? (
            <div className="text-center py-16 text-gray-600">
              <FontAwesomeIcon
                icon={faSpinner}
                className="text-mauritania-green-dark text-4xl animate-spin mb-4"
              />
              <p>{getTranslation("loading", language) || "جاري التحميل..."}</p>
            </div>
          ) : sousEtablissements.length > 0 ? (
            <>
              {/* Carousel Container */}
              <div className="relative px-4 sm:px-8">
                {/* Carousel Items */}
                <div className="overflow-hidden">
                  <div
                    className="flex transition-transform duration-500 ease-in-out"
                    style={{
                      transform: `translateX(-${
                        currentSousEtablissement * (100 / itemsPerSlide)
                      }%)`,
                    }}
                  >
                    {sousEtablissements.map((etablissement, index) => (
                      <div
                        key={etablissement.id}
                        className={`w-full md:w-1/5 px-3 flex-shrink-0 ${getSousEtablissementsStaggerAnimationClass(
                          index
                        )}`}
                      >
                        <div className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 flex flex-col h-full border border-gray-100 group">
                          {/* Logo Section */}
                          <div className="p-6 flex justify-center items-center h-48 bg-gradient-to-br from-gray-50 to-white relative overflow-hidden">
                            {/* Background Pattern */}
                            <div className="absolute inset-0 opacity-10">
                              <div className="absolute top-0 right-0 w-32 h-32 bg-mauritania-green rounded-full -mr-16 -mt-16"></div>
                              <div className="absolute bottom-0 left-0 w-24 h-24 bg-mauritania-gold rounded-full -ml-12 -mb-12"></div>
                            </div>

                            {etablissement.logo ? (
                              <div className="relative z-10 p-4 bg-white rounded-lg shadow-md group-hover:shadow-xl transition-shadow duration-300">
                                <img
                                  src={resolveMediaUrl(etablissement.logo)}
                                  alt={getText(
                                    etablissement.titrearabe,
                                    etablissement.titrefrancais
                                  )}
                                  className="h-20 w-auto object-contain max-w-full transition-transform duration-300 group-hover:scale-110"
                                  onError={(e) => {
                                    e.currentTarget.style.display = "none";
                                    e.currentTarget.parentElement!.innerHTML = `
                                <div class="text-5xl text-mauritania-green">
                                  <i class="fas fa-university"></i>
                                </div>
                              `;
                                  }}
                                />
                              </div>
                            ) : (
                              <div className="relative z-10">
                                <div className="w-24 h-24 rounded-full bg-gradient-to-br from-mauritania-green to-mauritania-green-dark flex items-center justify-center shadow-lg">
                                  <FontAwesomeIcon
                                    icon={faUniversity}
                                    className="text-white text-4xl"
                                  />
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Content Section */}
                          <div className="p-6 flex flex-col flex-grow">
                            <h3 className="text-xl font-bold text-mauritania-green-dark mb-4 text-center leading-tight min-h-[3.5rem] flex items-center justify-center">
                              <span className="group-hover:text-mauritania-green transition-colors duration-300">
                                {getText(
                                  etablissement.titrearabe,
                                  etablissement.titrefrancais
                                )}
                              </span>
                            </h3>

                            <div className="mt-auto pt-4 border-t border-gray-100">
                              <a
                                href={etablissement.lien}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center justify-center w-full bg-gradient-to-r from-mauritania-green-dark to-mauritania-green text-white font-semibold py-3 px-4 rounded-lg hover:from-mauritania-green hover:to-mauritania-green-dark transition-all duration-300 group shadow-md hover:shadow-lg transform hover:-translate-y-1"
                              >
                                <span className="mr-2 transition-transform duration-300 group-hover:scale-110">
                                  {getTranslation("visitWebsite", language) ||
                                    "زيارة الموقع"}
                                </span>
                                <FontAwesomeIcon
                                  icon={faExternalLinkAlt}
                                  className="text-sm transform transition-transform duration-300 group-hover:translate-x-1"
                                />
                              </a>
                            </div>
                          </div>

                          {/* Hover Effect Indicator */}
                          <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-mauritania-gold to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Carousel Navigation Buttons */}
                {sousEtablissements.length > itemsPerSlide && (
                  <>
                    <button
                      onClick={prevSousEtablissement}
                      className="absolute -left-4 sm:-left-8 top-1/2 transform -translate-y-1/2 bg-white text-mauritania-green-dark p-4 rounded-full shadow-xl hover:bg-mauritania-green hover:text-white transition-all duration-300 z-10 hover:scale-110 active:scale-95 group border border-gray-200"
                      aria-label={language === "ar" ? "السابق" : "Précédent"}
                      disabled={currentSousEtablissement === 0}
                    >
                      <FontAwesomeIcon
                        icon={faChevronLeft}
                        className={`w-5 h-5 transition-transform duration-300 group-hover:-translate-x-1 ${
                          language === "ar" ? "rotate-180" : ""
                        }`}
                      />
                    </button>
                    <button
                      onClick={nextSousEtablissement}
                      className="absolute -right-4 sm:-right-8 top-1/2 transform -translate-y-1/2 bg-white text-mauritania-green-dark p-4 rounded-full shadow-xl hover:bg-mauritania-green hover:text-white transition-all duration-300 z-10 hover:scale-110 active:scale-95 group border border-gray-200"
                      aria-label={language === "ar" ? "التالي" : "Suivant"}
                      disabled={
                        currentSousEtablissement >=
                        Math.ceil(sousEtablissements.length / itemsPerSlide) - 1
                      }
                    >
                      <FontAwesomeIcon
                        icon={faChevronRight}
                        className={`w-5 h-5 transition-transform duration-300 group-hover:translate-x-1 ${
                          language === "ar" ? "rotate-180" : ""
                        }`}
                      />
                    </button>
                  </>
                )}

                {/* Carousel Pagination with Numbers */}
                {sousEtablissements.length > itemsPerSlide && (
                  <div className="flex flex-col items-center mt-10">
                    {/* Dots Pagination */}
                    <div className="flex space-x-2 mb-4">
                      {Array.from({
                        length: Math.ceil(
                          sousEtablissements.length / itemsPerSlide
                        ),
                      }).map((_, index) => (
                        <button
                          key={index}
                          onClick={() => setCurrentSousEtablissement(index)}
                          className={`w-3 h-3 rounded-full transition-all duration-300 ${
                            index === currentSousEtablissement
                              ? "bg-mauritania-green-dark scale-125"
                              : "bg-gray-300 hover:bg-gray-400"
                          }`}
                          aria-label={`Page ${index + 1}`}
                        />
                      ))}
                    </div>

                    {/* Numbered Pagination */}
                    <div className="flex items-center space-x-4">
                      <span className="text-gray-600 text-sm font-medium">
                        {language === "ar" ? "صفحة" : "Page"}{" "}
                        {currentSousEtablissement + 1}{" "}
                        {language === "ar" ? "من" : "sur"}{" "}
                        {Math.ceil(sousEtablissements.length / itemsPerSlide)}
                      </span>

                      {/* Numbered Buttons */}
                      <div className="flex space-x-2">
                        {(() => {
                          const totalPages = Math.ceil(
                            sousEtablissements.length / itemsPerSlide
                          );
                          const pagesToShow = Math.min(5, totalPages);
                          let startPage = Math.max(
                            0,
                            currentSousEtablissement -
                              Math.floor(pagesToShow / 2)
                          );
                          startPage = Math.min(
                            startPage,
                            totalPages - pagesToShow
                          );

                          return Array.from(
                            { length: pagesToShow },
                            (_, i) => startPage + i
                          ).map((page) => (
                            <button
                              key={page}
                              onClick={() => setCurrentSousEtablissement(page)}
                              className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm font-medium transition-all duration-300 ${
                                page === currentSousEtablissement
                                  ? "bg-mauritania-green-dark text-white scale-110 shadow-md"
                                  : "bg-gray-100 text-gray-700 hover:bg-gray-200 hover:scale-105"
                              }`}
                            >
                              {page + 1}
                            </button>
                          ));
                        })()}
                      </div>
                    </div>
                  </div>
                )}

                {/* Items Counter */}
                <div className="text-center mt-6">
                  <p className="text-gray-500 text-sm">
                    {language === "ar" ? "عرض" : "Affichage"}{" "}
                    {Math.min(
                      currentSousEtablissement * itemsPerSlide + 1,
                      sousEtablissements.length
                    )}
                    -
                    {Math.min(
                      (currentSousEtablissement + 1) * itemsPerSlide,
                      sousEtablissements.length
                    )}{" "}
                    {language === "ar" ? "من" : "sur"}{" "}
                    {sousEtablissements.length}{" "}
                    {language === "ar" ? "مؤسسة" : "établissements"}
                  </p>
                </div>
              </div>
            </>
          ) : (
            <div className="text-center py-12 bg-white rounded-xl shadow-lg max-w-2xl mx-auto">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center mx-auto mb-6">
                <FontAwesomeIcon
                  icon={faUniversity}
                  className="text-gray-400 text-3xl"
                />
              </div>
              <h3 className="text-xl font-semibold text-gray-700 mb-3">
                {getTranslation("noSousEtablissements", language) ||
                  "لا توجد مؤسسات تحت الوصاية حالياً"}
              </h3>
              <p className="text-gray-500 max-w-md mx-auto mb-6">
                {language === "ar"
                  ? "سيتم إضافة المؤسسات تحت الوصاية قريباً. شكراً لتفهمكم."
                  : "Les établissements sous tutelle seront ajoutés prochainement. Merci de votre compréhension."}
              </p>
              <div className="w-32 h-1 bg-gradient-to-r from-transparent via-gray-300 to-transparent mx-auto"></div>
            </div>
          )}
        </div>
      </section>

      {/* Image Gallery Modal */}
      {selectedPhoto && selectedPhoto.fichiers && (
        <div
          className="fixed inset-0 bg-black bg-opacity-95 z-50 flex items-center justify-center p-4"
          onClick={closePhotoModal}
        >
          <div className="relative w-full max-w-7xl max-h-[90vh] overflow-y-auto">
            {/* Close Button */}
            <button
              className="absolute top-4 left-4 z-20 text-white bg-black bg-opacity-50 rounded-full p-3 hover:bg-opacity-70 transition-all"
              onClick={closePhotoModal}
            >
              <FontAwesomeIcon icon={faTimes} size="lg" />
            </button>

            {/* Navigation Arrows */}
            {selectedPhoto.fichiers.length > 1 && (
              <>
                <button
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 z-20 text-white bg-black bg-opacity-50 rounded-full p-4 hover:bg-opacity-70 transition-all hidden md:block"
                  onClick={(e) => {
                    e.stopPropagation();
                    prevImage();
                  }}
                >
                  <FontAwesomeIcon icon={faChevronLeft} size="lg" />
                </button>
                <button
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 z-20 text-white bg-black bg-opacity-50 rounded-full p-4 hover:bg-opacity-70 transition-all hidden md:block"
                  onClick={(e) => {
                    e.stopPropagation();
                    nextImage();
                  }}
                >
                  <FontAwesomeIcon icon={faChevronRight} size="lg" />
                </button>
              </>
            )}

            {/* Carousel Container */}
            <div
              className="bg-black rounded-lg overflow-hidden w-full h-full"
              onClick={(e) => e.stopPropagation()}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              {/* Main Image Display */}
              <div className="relative w-full h-[70vh] flex items-center justify-center">
                <img
                  src={getImageUrl(selectedPhoto.fichiers[currentImageIndex])}
                  alt={`${
                    language === "ar"
                      ? selectedPhoto.descriptionarabe
                      : selectedPhoto.descriptionfrancais
                  } - Image ${currentImageIndex + 1}`}
                  className="max-w-full max-h-full object-contain"
                />

                {/* Image Counter */}
                <div className="absolute top-4 right-4 bg-black bg-opacity-50 text-white px-3 py-1 rounded-lg text-sm">
                  {currentImageIndex + 1} / {selectedPhoto.fichiers.length}
                </div>

                {/* Mobile Navigation Buttons */}
                {selectedPhoto.fichiers.length > 1 && (
                  <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex items-center space-x-4 md:hidden">
                    <button
                      className="bg-black bg-opacity-50 text-white rounded-full p-3"
                      onClick={(e) => {
                        e.stopPropagation();
                        prevImage();
                      }}
                    >
                      <FontAwesomeIcon icon={faChevronLeft} />
                    </button>
                    <button
                      className="bg-black bg-opacity-50 text-white rounded-full p-3"
                      onClick={(e) => {
                        e.stopPropagation();
                        nextImage();
                      }}
                    >
                      <FontAwesomeIcon icon={faChevronRight} />
                    </button>
                  </div>
                )}
              </div>

              {/* Thumbnail Strip */}
              {selectedPhoto.fichiers.length > 1 && (
                <div className="bg-gray-900 p-4">
                  <div className="flex overflow-x-auto space-x-2 py-2">
                    {selectedPhoto.fichiers.map((image, index) => (
                      <button
                        key={index}
                        className={`flex-shrink-0 w-20 h-16 rounded overflow-hidden border-2 transition-all ${
                          index === currentImageIndex
                            ? "border-green-500"
                            : "border-transparent hover:border-white"
                        }`}
                        onClick={(e) => {
                          e.stopPropagation();
                          goToImage(index);
                        }}
                      >
                        <img
                          src={getImageUrl(image)}
                          alt={`Thumbnail ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Photo Details */}
              <div className="bg-white p-6">
                <div className="flex flex-col md:flex-row md:items-center justify-between mb-4">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-800">
                      {language === "ar"
                        ? selectedPhoto.descriptionarabe
                        : selectedPhoto.descriptionfrancais}
                    </h2>
                    <div className="flex flex-wrap gap-4 mt-2">
                      <div className="flex items-center text-gray-600">
                        <FontAwesomeIcon icon={faImages} className="ml-2" />
                        <span>{language === "ar" ? "صور" : "Photos"}</span>
                      </div>
                      <div className="text-gray-600">
                        {language === "ar" ? "رقم الترتيب:" : "Numéro d'ordre:"}{" "}
                        <span className="font-bold">{selectedPhoto.ordre}</span>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex items-center space-x-3 mt-4 md:mt-0">
                    <button
                      onClick={downloadImage}
                      className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                    >
                      <FontAwesomeIcon icon={faDownload} className="ml-2" />
                      {language === "ar" ? "تحميل الصورة" : "Télécharger"}
                    </button>

                    {/* Image Counter (for multiple images) */}
                    {selectedPhoto.fichiers.length > 1 && (
                      <div className="bg-gray-100 px-4 py-2 rounded-lg">
                        <span className="text-gray-700 font-medium">
                          {currentImageIndex + 1} /{" "}
                          {selectedPhoto.fichiers.length}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Navigation Dots */}
                {selectedPhoto.fichiers.length > 1 && (
                  <div className="flex justify-center space-x-2 mt-6">
                    {selectedPhoto.fichiers.map((_, index) => (
                      <button
                        key={index}
                        className={`w-3 h-3 rounded-full transition-all ${
                          index === currentImageIndex
                            ? "bg-green-600"
                            : "bg-gray-300 hover:bg-gray-400"
                        }`}
                        onClick={(e) => {
                          e.stopPropagation();
                          goToImage(index);
                        }}
                        aria-label={`Go to image ${index + 1}`}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Video Modal with Navigation */}
      {selectedVideo &&
        selectedVideo.liens &&
        selectedVideo.liens.length > 0 && (
          <div
            className="fixed inset-0 bg-black bg-opacity-95 z-50 flex items-center justify-center p-4"
            onClick={closeVideoModal}
          >
            <div className="relative w-full max-w-7xl max-h-[90vh] flex flex-col lg:flex-row gap-6">
              {/* Close Button */}
              <button
                className="absolute top-4 left-4 z-20 text-white bg-black bg-opacity-50 rounded-full p-3 hover:bg-opacity-70 transition-all"
                onClick={closeVideoModal}
              >
                <FontAwesomeIcon icon={faTimes} size="lg" />
              </button>

              {/* Main Video Area */}
              <div className="flex-1">
                <div
                  className="bg-black rounded-lg overflow-hidden relative"
                  onClick={(e) => e.stopPropagation()}
                >
                  {/* Video Player */}
                  {getCurrentVideoId() && (
                    <YouTubeEmbed
                      videoId={getCurrentVideoId()!}
                      autoplay={true}
                    />
                  )}

                  {/* Video Navigation Arrows (only for multiple videos) */}
                  {selectedVideo.liens.length > 1 && (
                    <>
                      <button
                        className="absolute left-4 top-1/2 transform -translate-y-1/2 z-20 text-white bg-black bg-opacity-50 rounded-full p-4 hover:bg-opacity-70 transition-all hidden lg:block"
                        onClick={(e) => {
                          e.stopPropagation();
                          prevVideo();
                        }}
                      >
                        <FontAwesomeIcon icon={faChevronLeft} size="lg" />
                      </button>
                      <button
                        className="absolute right-4 top-1/2 transform -translate-y-1/2 z-20 text-white bg-black bg-opacity-50 rounded-full p-4 hover:bg-opacity-70 transition-all hidden lg:block"
                        onClick={(e) => {
                          e.stopPropagation();
                          nextVideo();
                        }}
                      >
                        <FontAwesomeIcon icon={faChevronRight} size="lg" />
                      </button>

                      {/* Mobile Navigation Buttons */}
                      <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex items-center space-x-4 lg:hidden">
                        <button
                          className="bg-black bg-opacity-50 text-white rounded-full p-3"
                          onClick={(e) => {
                            e.stopPropagation();
                            prevVideo();
                          }}
                        >
                          <FontAwesomeIcon icon={faChevronLeft} />
                        </button>
                        <button
                          className="bg-black bg-opacity-50 text-white rounded-full p-3"
                          onClick={(e) => {
                            e.stopPropagation();
                            nextVideo();
                          }}
                        >
                          <FontAwesomeIcon icon={faChevronRight} />
                        </button>
                      </div>
                    </>
                  )}

                  {/* Video Counter */}
                  {selectedVideo.liens.length > 1 && (
                    <div className="absolute top-4 right-4 bg-black bg-opacity-50 text-white px-3 py-1 rounded-lg text-sm">
                      {currentVideoIndex + 1} / {selectedVideo.liens.length}
                    </div>
                  )}
                </div>

                {/* Video Details */}
                <div className="bg-white rounded-lg p-6 mt-4">
                  <div className="flex flex-col md:flex-row md:items-center justify-between mb-4">
                    <div>
                      <h2 className="text-2xl font-bold text-gray-800">
                        {language === "ar"
                          ? selectedVideo.descriptionarabe
                          : selectedVideo.descriptionfrancais}
                      </h2>
                      <div className="flex flex-wrap gap-4 mt-2">
                        <div className="flex items-center text-gray-600">
                          <FontAwesomeIcon icon={faVideo} className="ml-2" />
                          <span>
                            {language === "ar" ? "فيديوهات" : "Vidéos"}
                          </span>
                        </div>
                        <div className="text-gray-600">
                          {language === "ar"
                            ? "رقم الترتيب:"
                            : "Numéro d'ordre:"}{" "}
                          <span className="font-bold">
                            {selectedVideo.ordre}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex items-center space-x-3 mt-4 md:mt-0">
                      <a
                        href={getCurrentVideoUrl()}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                      >
                        <FontAwesomeIcon
                          icon={faExternalLinkAlt}
                          className="ml-2"
                        />
                        {language === "ar"
                          ? "فتح في يوتيوب"
                          : "Ouvrir sur YouTube"}
                      </a>

                      {/* Video Counter */}
                      {selectedVideo.liens.length > 1 && (
                        <div className="bg-gray-100 px-4 py-2 rounded-lg">
                          <span className="text-gray-700 font-medium">
                            {currentVideoIndex + 1} /{" "}
                            {selectedVideo.liens.length}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Navigation Dots */}
                  {selectedVideo.liens.length > 1 && (
                    <div className="flex justify-center space-x-2 mt-6">
                      {selectedVideo.liens.map((_, index) => (
                        <button
                          key={index}
                          className={`w-3 h-3 rounded-full transition-all ${
                            index === currentVideoIndex
                              ? "bg-red-600"
                              : "bg-gray-300 hover:bg-gray-400"
                          }`}
                          onClick={(e) => {
                            e.stopPropagation();
                            goToVideo(index);
                          }}
                          aria-label={`Go to video ${index + 1}`}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Video List Sidebar (only for multiple videos) */}
              {selectedVideo.liens.length > 1 && showVideoList && (
                <div className="lg:w-1/3">
                  <div
                    className="bg-gray-900 rounded-lg p-4 h-full overflow-y-auto"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-bold text-white flex items-center">
                        <FontAwesomeIcon icon={faList} className="ml-2" />
                        {language === "ar"
                          ? "قائمة الفيديوهات"
                          : "Liste des vidéos"}
                      </h3>
                      <button
                        onClick={() => setShowVideoList(false)}
                        className="text-white hover:text-gray-300 lg:hidden"
                      >
                        <FontAwesomeIcon icon={faTimes} />
                      </button>
                    </div>

                    <div className="space-y-3">
                      {selectedVideo.liens.map((link, index) => {
                        const videoId = getYouTubeId(link);
                        return (
                          <div
                            key={index}
                            className={`p-3 rounded-lg cursor-pointer transition-all ${
                              index === currentVideoIndex
                                ? "bg-red-600"
                                : "bg-gray-800 hover:bg-gray-700"
                            }`}
                            onClick={() => goToVideo(index)}
                          >
                            <div className="flex items-start">
                              {videoId && (
                                <div className="w-16 h-12 flex-shrink-0 mr-3">
                                  <img
                                    src={`https://img.youtube.com/vi/${videoId}/default.jpg`}
                                    alt=""
                                    className="w-full h-full object-cover rounded"
                                  />
                                </div>
                              )}
                              <div className="flex-1">
                                <div className="flex items-center justify-between">
                                  <span className="text-white text-sm font-medium">
                                    {language === "ar" ? "الفيديو" : "Vidéo"}{" "}
                                    {index + 1}
                                  </span>
                                  {index === currentVideoIndex && (
                                    <FontAwesomeIcon
                                      icon={faPlay}
                                      className="text-white text-xs ml-2"
                                    />
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

      <style>{`
        .animate-fade-in-up {
          animation: fadeInUp 0.8s ease-out forwards;
        }
        
        .delay-100 {
          animation-delay: 0.1s;
        }
        
        .delay-200 {
          animation-delay: 0.2s;
        }
        
        .delay-300 {
          animation-delay: 0.3s;
        }
        
        .delay-400 {
          animation-delay: 0.4s;
        }
        
        .delay-500 {
          animation-delay: 0.5s;
        }
        
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        .line-clamp-3 {
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        /* Smooth scrolling for the entire page */
        html {
          scroll-behavior: smooth;
        }
      `}</style>
    </div>
  );
};

export default Home;
